"use client";

import clsx from "clsx";
import {
  RealWorldMap,
  CORRIDOR_VIEWPORT,
  project,
} from "@/components/scenes/shared/RealWorldMap";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Corridor map — a geographically accurate (recognisable-continent) inline
 * SVG of the BR↔UAE corridor with port markers, three milestone glow
 * indicators (Loading · Departure · Arrival) and an animated vessel that
 * lerps along the curved Cape route as `progress` advances.
 *
 * Real-world ports projected via equirectangular helper from
 * `RealWorldMap` (no map lib, no tile server).
 *
 * Props:
 *   - direction: "br-to-ae" (T1/T2) or "ae-to-br" (T3) — flips vessel travel
 *   - progress:  0 (origin) → 1 (destination)
 *   - reached:   set of Loading | Departure | Arrival → glow the milestone
 */
export function CorridorMap({
  direction = "br-to-ae",
  progress = 0,
  reached,
  modality = "sea",
  className,
}: {
  direction?: "br-to-ae" | "ae-to-br";
  progress?: number;
  reached: Set<string>;
  /** "sea" (default) renders the Cape route + vessel marker. "air"
   *  swaps to a great-circle arc Viracopos → Doha → Dubai with an ✈
   *  glyph. T4 7.9 sets modality="air". */
  modality?: "sea" | "air";
  className?: string;
}) {
  const t = useTranslations("corridorMap");
  const vp = CORRIDOR_VIEWPORT;
  const isAir = modality === "air";

  // Real lat/lon for the corridor ports / airports.
  const SANTOS_LL    = { lon: -46.33, lat: -23.96 };  // T1/T2 sea origin
  const JEBEL_ALI_LL = { lon:  55.06, lat:  25.01 };  // T1/T2 sea destination
  const VCP_LL       = { lon: -47.13, lat: -22.99 };  // T4 air origin (Viracopos)
  const DOH_LL       = { lon:  51.61, lat:  25.27 };  // T4 air hub (Doha)
  const DXB_LL       = { lon:  55.36, lat:  25.25 };  // T4 air destination (Dubai)

  const origin       = isAir ? project(VCP_LL.lon, VCP_LL.lat, vp) : project(SANTOS_LL.lon, SANTOS_LL.lat, vp);
  const destination  = isAir ? project(DXB_LL.lon, DXB_LL.lat, vp) : project(JEBEL_ALI_LL.lon, JEBEL_ALI_LL.lat, vp);
  // Alias for legacy code below.
  const santos       = origin;
  const jebelAli     = destination;

  // Departure marker — small offshore offset east into the Atlantic so the
  // eye reads "vessel just left the loading berth". 4° east of Santos
  // (~ 450 km) is enough to space it visually without leaving the curve.
  const departureLL = { lon: SANTOS_LL.lon + 4, lat: SANTOS_LL.lat - 1 };
  const departure   = project(departureLL.lon, departureLL.lat, vp);

  // Route paths — sea uses the Cape detour (south of Africa); air uses a
  // more direct two-leg great-circle via Doha (mid-East hub).
  let ROUTE_PATH: string;
  if (isAir) {
    const doha = project(DOH_LL.lon, DOH_LL.lat, vp);
    // Two great-circle segments: VCP → DOH (cross-Atlantic + Sahara), DOH → DXB (short Gulf hop).
    // Use a control point lifted northward to suggest the arc.
    const arc1 = project(0, 15, vp);   // mid-Atlantic / Sahara apex
    ROUTE_PATH = `M ${origin.x.toFixed(1)} ${origin.y.toFixed(1)}
                  Q ${arc1.x.toFixed(1)} ${arc1.y.toFixed(1)} ${doha.x.toFixed(1)} ${doha.y.toFixed(1)}
                  L ${destination.x.toFixed(1)} ${destination.y.toFixed(1)}`;
  } else {
    // Cape route — Bezier (two-quadratic) passing south of Africa via a
    // control point near the Cape of Good Hope (20°E, -38°S) and a second
    // control midway across the Indian Ocean (45°E, -25°S).
    const cape   = project(20, -42, vp);
    const midIO  = project(48, -8,  vp);
    ROUTE_PATH = `M ${santos.x.toFixed(1)} ${santos.y.toFixed(1)}
                  Q ${cape.x.toFixed(1)} ${cape.y.toFixed(1)} ${midIO.x.toFixed(1)} ${midIO.y.toFixed(1)}
                  Q ${(midIO.x + 30).toFixed(1)} ${(midIO.y - 30).toFixed(1)} ${jebelAli.x.toFixed(1)} ${jebelAli.y.toFixed(1)}`;
  }

  // Vessel position lerp along the route. For sea: two-quad bezier
  // (Cape detour). For air: simple linear interpolation from origin to
  // destination via Doha midpoint (close enough at this scale).
  const tProgress = direction === "ae-to-br" ? 1 - progress : progress;
  const vessel = isAir
    ? linearAt(tProgress, origin, destination)
    : (() => {
        const cape   = project(20, -42, vp);
        const midIO  = project(48, -8,  vp);
        return vesselAt(tProgress, santos, cape, midIO, jebelAli);
      })();

  // Milestone glow logic — uniform "ae-to-br swaps Loading↔Arrival
  // visually too" so the loading port is at the actual origin port for the
  // active direction. We don't relabel; the underlying scenario already
  // emits Loading at the origin in either direction.
  const loadingAt    = direction === "ae-to-br" ? jebelAli : santos;
  const arrivalAt    = direction === "ae-to-br" ? santos   : jebelAli;
  const departureAt  = isAir
    ? project(DOH_LL.lon, DOH_LL.lat, vp)
    : direction === "ae-to-br"
      ? project(JEBEL_ALI_LL.lon - 4, JEBEL_ALI_LL.lat - 1, vp)
      : departure;

  const loadingGlow   = reached.has("Loading");
  const departureGlow = reached.has("Departure");
  const arrivalGlow   = reached.has("Arrival");

  return (
    <svg viewBox={`0 0 ${vp.w} ${vp.h}`} className={clsx("h-full w-full", className)}>
      <RealWorldMap vp={vp} />

      <defs>
        <radialGradient id="port-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="var(--state-traced)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--state-traced)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Route — dashed underlay then solid overlay sized to t */}
      <path d={ROUTE_PATH} stroke="var(--state-traced-edge)" strokeWidth="2"   fill="none" strokeDasharray="4 4" />
      <path d={ROUTE_PATH} stroke="var(--state-traced)"      strokeWidth="2.5" fill="none" strokeDasharray={`${tProgress * 900} 1500`} opacity="0.85" />

      {/* Loading milestone — at origin port */}
      <MilestoneMarker
        x={loadingAt.x}
        y={loadingAt.y}
        label={isAir ? "Viracopos · VCP · BR" : direction === "ae-to-br" ? "Abu Dhabi Port · UAE" : "Santos · BR"}
        milestone="LOADING"
        glow={loadingGlow}
      />

      {/* Departure milestone — offshore for sea, Doha hub for air */}
      <MilestoneMarker
        x={departureAt.x}
        y={departureAt.y}
        label={isAir ? "Doha · DOH · QA" : ""}
        milestone={isAir ? "HUB" : "DEPARTURE"}
        glow={departureGlow}
        small={!isAir}
      />

      {/* Arrival milestone — at destination port */}
      <MilestoneMarker
        x={arrivalAt.x}
        y={arrivalAt.y}
        label={isAir ? "Dubai Cargo · DXB · UAE" : direction === "ae-to-br" ? "Itaqui · BR" : "Jebel Ali · UAE"}
        milestone="ARRIVAL"
        glow={arrivalGlow}
      />

      {/* Vessel / aircraft marker (only after Loading has fired) */}
      {loadingGlow && (
        <g transform={`translate(${vessel.x}, ${vessel.y})`}>
          <circle r="14" fill="var(--state-traced-glow)" />
          {isAir ? (
            <AircraftGlyph />
          ) : (
            <>
              <circle r="6"  fill="var(--state-traced)" />
              <circle r="3"  fill="var(--orbitra-paper)" />
            </>
          )}
          <text
            x="0"
            y="-22"
            textAnchor="middle"
            fill="var(--state-traced)"
            fontSize="9"
            fontFamily="var(--font-mono)"
            fontWeight="600"
          >
            {arrivalGlow ? t("statusArrived") : departureGlow ? (isAir ? t("statusInFlight") : t("statusAtSea")) : t("statusLoading")}
          </text>
        </g>
      )}
    </svg>
  );
}

function MilestoneMarker({
  x,
  y,
  label,
  milestone,
  glow,
  small = false,
}: {
  x: number;
  y: number;
  label: string;
  milestone: "LOADING" | "DEPARTURE" | "ARRIVAL" | "HUB";
  glow: boolean;
  small?: boolean;
}) {
  const r = small ? 4 : 6;
  return (
    <g transform={`translate(${x}, ${y})`}>
      {glow && <circle r={small ? 12 : 18} fill="url(#port-glow)" />}
      <circle r={r} fill={glow ? "var(--state-traced)" : "var(--state-cold)"} stroke="var(--orbitra-paper)" strokeWidth="1.2" />
      {label && (
        <text x="0" y={-r - 8} textAnchor="middle" fill="var(--orbitra-teal-soft)" fontSize="10" fontFamily="var(--font-mono)">
          {label}
        </text>
      )}
      <text
        x="0"
        y={r + 14}
        textAnchor="middle"
        fill={glow ? "var(--state-traced)" : "var(--orbitra-muted)"}
        fontSize="8"
        fontFamily="var(--font-mono)"
        fontWeight={glow ? 700 : 500}
        letterSpacing="0.08em"
      >
        {milestone}
      </text>
    </g>
  );
}

/**
 * Lerp vessel position along the same two-quadratic bezier the route uses.
 * Mirrors the route's control points so the vessel sits exactly on the
 * drawn curve.
 */
function vesselAt(
  t: number,
  p0: { x: number; y: number },
  c1: { x: number; y: number },
  mid: { x: number; y: number },
  p2: { x: number; y: number },
): { x: number; y: number } {
  if (t < 0) t = 0;
  if (t > 1) t = 1;
  if (t < 0.5) {
    const u = t / 0.5;
    return quad(p0, c1, mid, u);
  }
  // Second segment uses mid → (mid + offset) → p2; recompute the
  // control point exactly as the route does.
  const c2 = { x: mid.x + 30, y: mid.y - 30 };
  const u = (t - 0.5) / 0.5;
  return quad(mid, c2, p2, u);
}

function quad(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  const inv = 1 - t;
  return {
    x: inv * inv * p0.x + 2 * inv * t * p1.x + t * t * p2.x,
    y: inv * inv * p0.y + 2 * inv * t * p1.y + t * t * p2.y,
  };
}

/** Straight-line lerp for air-modality route (simpler than the sea
 *  Cape-route bezier; the aircraft glyph follows the great-circle
 *  visually without us needing to compute it exactly). */
function linearAt(
  t: number,
  p0: { x: number; y: number },
  p1: { x: number; y: number },
): { x: number; y: number } {
  if (t < 0) t = 0;
  if (t > 1) t = 1;
  return { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t };
}

/**
 * AircraftGlyph — small bespoke widebody-freighter silhouette for the
 * vessel marker on air-modality routes. Replaces the earlier ✈ unicode
 * glyph: nose right, swept wings, vertical tail. Sized to sit inside
 * the existing 14px state-traced-glow circle without overflowing it.
 */
function AircraftGlyph() {
  return (
    <g>
      {/* Fuselage — nose-right capsule */}
      <path
        d="M -9 0 Q -9 -2.2 -6.5 -2.2 L 8 -2.2 Q 11 -2.2 12 0 Q 11 2.2 8 2.2 L -6.5 2.2 Q -9 2.2 -9 0 Z"
        fill="var(--state-traced)"
      />
      {/* Vertical tail fin (left side) */}
      <path d="M -9 -2 L -11 -7 L -7.5 -7 L -6 -2 Z" fill="var(--state-traced)" />
      {/* Horizontal stabiliser (small wing at tail) */}
      <path d="M -9 0 L -12 2 L -8 2 Z" fill="var(--state-traced)" opacity="0.85" />
      {/* Main wing — swept back, slightly behind centre */}
      <path d="M -1 -1 L 5 -1 L 7 4 L -3 4 Z" fill="var(--state-traced)" opacity="0.95" />
      {/* Cockpit window dot */}
      <circle cx="9.5" cy="-0.4" r="0.9" fill="var(--orbitra-paper)" />
      {/* Cabin window strip (very subtle) */}
      <rect x="-2" y="-0.8" width="8" height="0.7" fill="var(--orbitra-paper)" opacity="0.45" />
    </g>
  );
}
